import {
  DataGrid,
  GridEventListener,
  GridRowClassNameParams,
  GridSortModel,
  GridValidRowModel,
} from "@mui/x-data-grid";

import { useEffect, useRef, useState, useMemo, Dispatch, SetStateAction } from "react";

import {
  openInvoiceDetail,
  selectInvoiceTableColumns,
  selectInvoiceTableHiddenColumns,
  updateInvoiceTableColumns,
  updateInvoiceTableHiddenColumns,
  selectLGInternalUser
} from "../../activeScreenSlice";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useGetInvoicefilterDrpdown, useInvoiceQueue } from "../../hooks";
import { gridColumns } from "./InvoicesTable.schema";

import {
  DataGridWrapper,
  DraggedColumn,
  DropLine,
  PickupBox,
  PickupDropContainer,
} from "../SharedGridStyles/SharedGrid.styles";

import { Box, Typography, CircularProgress, Button } from "@mui/material";
import UnassignModal from "../UnassignModal/UnassignModal";
import MoveInvoiceModal from "../MoveInvoiceModal/MoveInvoiceModal";
import RejectModal from "../RejectModal/RejectModal";
import { dateOperators } from "./customDateOperators";
import { adjustForWeekend, useFilteredInvoiceCounts } from "../../hooks/useFilteredInvoiceCounts";
import { UserRole, StatusGroup, InvoiceAgeGroup } from "../../api/generated";
import { useCurrentUser, useGetUsers } from "../../hooks/Users";
import useShowError from "../../hooks/useShowError";
import { useInvoiceIds } from "../../hooks";
import InvoiceFilterPanel, { FilterValues } from "../InvoicesTable/InvoiceFilterPanel";
import { useQueryClient } from "@tanstack/react-query";
import { QUEUE_STATISTICS_QUERY_KEY } from "../../constants";
import { useWorkLists } from "../../hooks/WorkLists";
import { useGetAllOpco } from "../../hooks/OpCo";

const columnMapping = {
  external_invoice_id: "externalInvoiceId",
  assigned_user_email: "assignedTo",
  supplier_name: "supplierName",
  supplier_site_code: "supplierSite",
  invoice_date: "invoiceDate",
  gl_date: "glDate",
  total_amount: "invoiceAmount",
  queue_name: "queueName",
  currency: "currency",
  business_unit: "opcoName",
};
const initialFilterValues: FilterValues = {
  businessUnit: null,
  queueName: null,
  assignedUserEmail: null,
  invoiceStartDate: null,
  invoiceEndDate: null,
  invoiceAge: null,
  currency: null,
  invoiceAmountRange: null,
  supplierName: null,
  supplierSite: null,
};

interface InvoicesTableProps {
  resetActiveFilter: () => void;
  dateFilter: any;
  totalInvoicesCount: number;
  setInvoiceCounts: Dispatch<SetStateAction<{
    invoice_count: number;
    today: number;
    oneToSevenDays: number;
    eightToFifteenDays: number;
    sixteenPlusDays: number;
    outOfSLA: number;
  }>>;
  lastFilterSource: string | null;
  userRole?: string | null;
  setInvFilters:(filters)=>void;
  filters:FilterValues,
  setFilters:(FilterValues)=>void
  filteredData:{},
  setFilteredData:(filters)=>void
  sortModel: GridSortModel;
  setSortModel: React.Dispatch<React.SetStateAction<GridSortModel>>;
  paginationModel:{page:number,pageSize:number};
  setPaginationModel:(prev)=>void;
}
export interface InvoiceQueueFilters {
  pageNum?: number;
  limit?: number;
  accessToken?: string;
  assignedUserEmail?: string | null;
  arrowId?: number | null,
  invoiceNumber?: string | null,
  supplierName?: string | null;
  supplierSite?: string | null;
  currency?: string | null;
  queueName?: string | null;
  businessUnit?: string | null;
  invoiceAge?: InvoiceAgeGroup | null;
  invoiceStartDate?: string | null;
  invoiceEndDate?: string | null;
  glStartDate?: string | null;
  glEndDate?: string | null;
  invoiceAmountMin?: number | null;
  invoiceAmountMax?: number | null;
  sortOrder?: 'asc' | 'desc';
  sortBy?:string | null;

}
export default function InvoicesTable({
  resetActiveFilter,
  dateFilter,
  setInvoiceCounts,
  lastFilterSource,
  totalInvoicesCount,
  userRole,
  setInvFilters,
  filters,
  setFilters,
  filteredData,
  setFilteredData,
  sortModel,
  setSortModel,
  paginationModel,
  setPaginationModel

}: InvoicesTableProps) {
  const dispatch = useAppDispatch();
  const showError = useShowError();
  const LGInternalUser = useAppSelector(selectLGInternalUser);

  // Pass filters to useInvoiceQueue!
  function toSnakeCase(str) {
    return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
  }
  function mapFilterValuesToApi(filters: FilterValues): InvoiceQueueFilters {
    // Map invoiceAmountRange to min/max
    let invoiceAmountMin: number | null = null;
    let invoiceAmountMax: number | null = null;
    switch (filters.invoiceAmountRange) {
      case "0 to 5,000":
        invoiceAmountMin = 0;
        invoiceAmountMax = 5000;
        break;
      case "5,001 to 10,000":
        invoiceAmountMin = 5001;
        invoiceAmountMax = 10000;
        break;
      case "≥ 10,000":
        invoiceAmountMin = 10001;
        invoiceAmountMax = null;
        break;
      case "≤ −10,000": // More negative than -10,000
        invoiceAmountMin = null;
        invoiceAmountMax = -10001;
        break;
      case "-10,000 to -5,001":
        invoiceAmountMin = -10000;
        invoiceAmountMax = -5001;
        break;
      case "-5,000 to -1":
        invoiceAmountMin = -5000;
        invoiceAmountMax = -1;
        break;
    }



    // Only include invoiceAge if set by user
    let invoiceAge: InvoiceAgeGroup | null = null;
    if (filters.invoiceAge) {
      switch (filters.invoiceAge) {
        case "Received Today":
          invoiceAge = InvoiceAgeGroup.RECEIVED_TODAY;
          break;
        case "1-7 days":
          invoiceAge = InvoiceAgeGroup._1_7_DAYS;
          break;
        case "8-15 days":
          invoiceAge = InvoiceAgeGroup._8_15_DAYS;
          break;
        case "16+ days":
          invoiceAge = InvoiceAgeGroup._16_DAYS;
          break;
        case "ALL":
          invoiceAge = InvoiceAgeGroup.ALL;
          break;
        default:
          invoiceAge = null;
      }
    }

    return {
      arrowId: filters.arrowId || null,
      invoiceNumber: filters.invoiceNumber ||null,
      assignedUserEmail: filters.assignedUserEmail || null,
      supplierName: filters.supplierName || null,
      supplierSite: filters.supplierSite || null,
      currency: filters.currency || null,
      queueName: filters.queueName || null,
      businessUnit: filters.businessUnit || null,
      invoiceAge,
      invoiceStartDate: filters.invoiceStartDate || null,
      invoiceEndDate: filters.invoiceEndDate || null,
      glStartDate: filters.glStartDate || null,
      glEndDate: filters.glEndDate || null,
      invoiceAmountMin,
      invoiceAmountMax,
    };
  }

  const getSortParams = () => {
    if (!sortModel.length) return {};
    const { field, sort } = sortModel[0];
    const snakeCaseField = Object.entries(columnMapping).find(
      ([snake, camel]) => camel === field
    )?.[0] || toSnakeCase(field);
    return {
      sortBy: snakeCaseField,
      sortOrder: sort,
    };
  };

 const apiFilters = useMemo(() => {
  const sortParams = getSortParams();
  return {
    ...mapFilterValuesToApi({ ...filteredData }),
    ...sortParams,
    pageNum: paginationModel.page,
    limit: paginationModel.pageSize,
  };
 }, [filteredData, sortModel, paginationModel]);


  const { data, isLoading, isError, error } = useInvoiceQueue(
    StatusGroup.REVIEW,
    apiFilters

  );

  const [rowSelectionModel, setRowSelectionModel] = useState<any[]>([]);
  const [moveInvoiceModalOpen, setMoveInvoiceModalOpen] = useState(false);
  const [unassignModalOpen, setUnassignModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);

  const [pickedColumn, setPickedColumn] = useState<string | null>(null);
  const [pickedColumnTitle, setPickedColumnTitle] = useState<string | null>(null);
  const [hoverColumn, setHoverColumn] = useState<string | null>(null);
  const [dropColumn, setDropColumn] = useState<string | undefined>(undefined);

  const currentUser = useCurrentUser();
  const invById = useInvoiceIds(rowSelectionModel!);

  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const dragElementRef = useRef<HTMLDivElement>(null);

  const persistedGridColumns = useAppSelector(selectInvoiceTableColumns);
  const persistedGridHiddenColumns = useAppSelector(selectInvoiceTableHiddenColumns);

  // Data formatting only, no local filtering!
  function dataRowMapping(data: any[]): GridValidRowModel[] {
    return data?.map((record, idx) => {
      // Compose a fallback unique key if record.id is missing or not unique
      let rowId = record.id?.toString();
      if (!rowId || rowId === "") {
        // Fallback: use external_invoice_id + idx or just idx
        rowId = (record.external_invoice_id ? record.external_invoice_id.toString() : "") + "_" + idx;
      }
      return {
        id: rowId,
        assignedTo: record.assigned_user_email || "",
        supplierName: record.supplier_name || "",
        supplierSite: record.supplier_site_code || "",
        invoiceDate: record.invoice_date
          ? adjustForWeekend(new Date(record.invoice_date).toISOString().split("T")[0])
          : "",
        glDate: record.gl_date
          ? adjustForWeekend(new Date(record.gl_date).toISOString().split("T")[0])
          : "",
        invoiceAmount: record.total_amount || "",
        currency: record.currency || "",
        queueName: record.queue_name || "",
        opcoName: record.opco?.name || "",
        externalInvoiceId: record.external_invoice_id || "",
      };
    });
  }

  function isAnyFilterSet(filters: FilterValues) {
    // Only show clear button if any filter differs from initialFilterValues
    return Object.keys(initialFilterValues).some(
      (key) => filters[key] !== initialFilterValues[key]
    );
  }
  const formattedData: GridValidRowModel[] = useMemo(() => dataRowMapping(data ?? []), [data]);

const dropdownFields = ["currency", "assignedTo", "queueName", "opcoName"];
const users = useGetUsers();
const workLists = useWorkLists()
const allOpcosQuery = useGetAllOpco()

const invoiceFilterDropDowns = useGetInvoicefilterDrpdown(StatusGroup.REVIEW)

const dropdownOptions = useMemo(() => {
  // Defensive: fallback to empty array if any field is missing
  const {
    currencies = [],
    assigned_to = [],
    worklists = [],
    business_units = [],
  } = invoiceFilterDropDowns?.data || {};

  return {
    currency: currencies.sort((a, b) => a.localeCompare(b)),
    assignedTo: assigned_to.sort((a, b) => a.localeCompare(b)),
    queueName: worklists.sort((a, b) => a.localeCompare(b)),
    opcoName: business_units.sort((a, b) => a.localeCompare(b)),
  };
}, [invoiceFilterDropDowns]);


  // Drag-and-drop column functionality
  useEffect(() => {
    if (dropColumn) {
      const found = persistedGridColumns.find(
        (col) => col.field === pickedColumn,
      );
      if (found) {
        const updatedColumns = persistedGridColumns.filter(
          (col) => col.field !== pickedColumn,
        );
        const dropIndex = updatedColumns.findIndex(
          (col) => col.field === dropColumn,
        );
        updatedColumns.splice(dropIndex, 0, found);
        setDropColumn(undefined);
        dispatch(updateInvoiceTableColumns(updatedColumns));
      }
    }
    // eslint-disable-next-line
  }, [pickedColumn, dropColumn, persistedGridColumns, dispatch]);

  const handleRowClick: GridEventListener<"rowClick"> = (params) => {
    if (
      (userRole?.toLowerCase() === UserRole.USER &&
        (currentUser?.data?.email === params?.row?.assignedTo ||
          params?.row?.assignedTo === null || params?.row?.assignedTo === "")) ||
      userRole?.toLowerCase() === UserRole.TEAM_LEAD ||
      userRole?.toLowerCase() === UserRole.CLIENT_ADMIN ||
      userRole?.toLowerCase() === UserRole.SYSTEM_ADMIN
    ) {
      dispatch(openInvoiceDetail(params.row.id));
    } else {
      showError("Reviewing invoices that are not issued in your name is not authorized.");
    }
  };

  const handleColumnHeaderClick = (
    event: React.MouseEvent,
    column: string,
    title: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setPickedColumn(column);
    setPickedColumnTitle(title);
    setDragPosition({ x: event.clientX, y: event.clientY });
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (event: MouseEvent) => {
    setDragPosition({ x: event.clientX, y: event.clientY });
  };

  const handleMouseUp = () => {
    setPickedColumn(null);
    setPickedColumnTitle(null);
    setHoverColumn(null);
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  const handleMoveInvClick = () => {
    invById.refetch();
    setMoveInvoiceModalOpen(true);
  };

  // Handler to update filters from InvoiceFilterPanel
  const handleFilterSearch = (filters: any) => {
    setFilteredData(filters);
    setPaginationModel((prev) => ({ ...prev, page: 0 })); // Reset to first page on new filter
  };

  const columnsWithRuntimeValues = useMemo(() => {
    return persistedGridColumns.map((column) => {
      if (column.field === "invoiceDate" || column.field === "glDate") {
        return {
          ...column,
          valueGetter: ({ value }) => (value ? new Date(value) : null),
          filterOperators: dateOperators,
        };
      }
      return column;
    });
  }, [persistedGridColumns]);

  if (isError) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          Error loading invoices: {error.message}
        </Typography>
      </Box>
    );
  }

  const noDataStyles = {
    height: formattedData.length < 1 ? 600 : undefined,
    width: formattedData.length < 1 ? "100%" : undefined,
  };


  return (
    <>
      <MoveInvoiceModal
        open={moveInvoiceModalOpen}
        onClose={() =>{
          setMoveInvoiceModalOpen(false);
          setRowSelectionModel([]);
        } 
        }
        invoiceIds={rowSelectionModel as number[]}
      />
      <UnassignModal
        open={unassignModalOpen}
        onClose={() => {
          setUnassignModalOpen(false);
          setRowSelectionModel([]);
        }}
        invoiceIds={rowSelectionModel as number[]}
      />
      <RejectModal
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        invoiceIds={rowSelectionModel as number[]}
        onRejectionSubmitted={() => setRowSelectionModel([])}
      />

      {/* Filter Panel */}
      <Box sx={{ mb: 2 }}>
        <InvoiceFilterPanel
          filters={filters}
          dropdownOptions={dropdownOptions}
          onSearch={() => {
              if (filters.arrowId && !/^\d+$/.test(filters.arrowId.toString())) {
              showError("Arrow ID must be a number");
              return;
              }
              if (filters.invoiceNumber && !/^\d+$/.test(filters.invoiceNumber.toString())) {
              showError("InvoiceNumber must be a number");
              return;
              }
            setFilteredData(filters);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            setInvFilters(mapFilterValuesToApi({ ...filters }))
          }}
          onFilterChange={(newFilters) => {
            setFilters(newFilters);
          }}
          onClear={() => {
            setFilters(initialFilterValues);
            setFilteredData(initialFilterValues);
            setSortModel([]);
            setPaginationModel((prev) => ({ pageSize: 10, page: 0 }));
            setInvFilters({})
          }}
          showClearResultsButton={true}
          resetActiveFilter={resetActiveFilter}
          dateFilter={dateFilter}
          lastFilterSource={lastFilterSource}
        />
      </Box>

      {/* Table Action Bar */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ flex: "1" }} />
        {!!rowSelectionModel.length && (
          <Box sx={{ display: "flex", gap: "3px", mb: 2, }}>
            <Typography
              sx={{
                textAlign: "center",
                fontSize: "32px",
                fontWeight: 400,
                lineHeight: "40px",
              }}
            >
              {rowSelectionModel.length}
            </Typography>
            <Typography
              sx={{
                color: "var(--grey-grey-06)",
                textAlign: "center",
                fontSize: "12px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "16px",
                marginRight: "10px",
                marginTop: "4px",
              }}
            >
              items selected
            </Typography>
            {(userRole?.toLowerCase() === UserRole.TEAM_LEAD ||
              userRole?.toLowerCase() === UserRole.CLIENT_ADMIN ||
              userRole?.toLowerCase() === UserRole.SYSTEM_ADMIN) && (
                <>
                  <Button
                      variant="outlined"
                      onClick={() => setUnassignModalOpen(true)}
                      sx={{ marginRight: "10px" }}
                      disabled={LGInternalUser}
                  >
                      Unassign
                  </Button>
                  <Button
                      variant="outlined"
                      onClick={handleMoveInvClick}
                      sx={{ marginRight: "10px" }}
                      disabled={LGInternalUser}
                  >
                      Move to
                  </Button>
                  <Button
                      variant="outlined"
                      sx={{borderColor: "var(--cherry) !important", color: "var(--cherry)",}}
                      onClick={() => setRejectModalOpen(true)}
                      disabled={LGInternalUser}
                  >
                      Reject
                  </Button>
                </>
              )}
          </Box>
        )}
      </Box>
      {
        isLoading ? <CircularProgress sx={{margin: '0 auto', mt: "2%"}}/> : (
          <Box sx={{ ...noDataStyles }}>
            <DataGrid<GridValidRowModel>
              sx={{
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                },
                backgroundColor: 'white',
                '& .MuiDataGrid-row.even-row': {
                  backgroundColor: 'var(--background-off-white)',
                }
              }}
              getRowClassName={(e: GridRowClassNameParams<any>) => e.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"}
              rows={isLoading ? [] : formattedData}
              rowCount={Number(totalInvoicesCount)}
              onPaginationModelChange={setPaginationModel}
              keepNonExistentRowsSelected
              columns={columnsWithRuntimeValues.map((column) => ({
                ...column,
              }))}
              paginationModel={paginationModel}
              paginationMode="server"
              pageSizeOptions={[10, 20, 50, 100]}
              onRowClick={handleRowClick}
              loading={isLoading}
              checkboxSelection
              onRowSelectionModelChange={(newRowSelectionModel) => {
                setRowSelectionModel(newRowSelectionModel);
              }}
              rowSelectionModel={rowSelectionModel}
              columnVisibilityModel={persistedGridHiddenColumns}
              onColumnVisibilityModelChange={(newColumnVisibilityModel) =>
                dispatch(updateInvoiceTableHiddenColumns(newColumnVisibilityModel))
              }
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={setSortModel}

            />
          </Box>
        )}
      {/* Dragged column visual (for DnD columns) */}
      {pickedColumn && (
        <DraggedColumn
          ref={dragElementRef}
          style={{
            top: dragPosition.y,
            left: dragPosition.x,
            position: "fixed",
            pointerEvents: "none",
            zIndex: 9999,
          }}
        >
          {pickedColumnTitle}
        </DraggedColumn>
      )}
    </>
  );
}
